import { useTranslation } from "react-i18next";
import type { SupportedLanguage } from "../../../interfaces";
import { apiClient } from "../../../services/api";
import { reloadCurrentUser } from "../../user/hooks/useCurrentUser";
import { displayToast } from "../../../components/app/AppToast";
import { useActiveLang } from "./useActiveLang";
import { storePublicLang } from "../utils/langUtils";

/**
 * Switch the app language for a signed-in reader.
 *
 * Four steps, one operation. The account carries the language (it decides the
 * language of the emails and the pushes), i18next carries it for this render,
 * `storePublicLang` carries it for the logged-out pages, and `/users/me` has to
 * be re-read because half the app's labels are served translated. A picker that
 * did three of the four looks correct until the reader signs out or opens an
 * email.
 *
 * Two pickers write this now — the mobile sheet and the desktop settings panel —
 * which is what makes it a rule rather than a screen's business.
 */
export function useChangeLanguage(): (
  lang: SupportedLanguage
) => Promise<void> {
  const { t } = useTranslation();
  const { activeLang, changeActiveLang } = useActiveLang();

  return async function changeLanguage(lang: SupportedLanguage): Promise<void> {
    if (lang === activeLang) return;
    try {
      await apiClient.patch("/webapp/private/users/me", {
        json: { language: lang },
      });
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
      return;
    }
    changeActiveLang(lang);
    storePublicLang(lang);
    void reloadCurrentUser();
    displayToast(t("Settings updated"), "success");
  };
}
